import '../Graphs.css'

import { useEffect, useRef, useState } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts";

export const TrendPeriodoChart = (props) => {

    const [dataset, setDataset] = useState([])
    const barRef = useRef(null);
    const modifierKeyRef = useRef(false); // Traccia se Ctrl/Cmd è premuto
    const { onViewTypeChange, onChartFilterChange, onLegendChange, onDownload, extraButton } = props;

    /**
     * Formatta i numeri con separatore migliaia (.) e decimali (,)
     * Massimo 2 cifre decimali con arrotondamento
     */
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(num);
    };

    const [filteredDataset, setFilteredDataset] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState([]); // Array di periodi selezionati per multi-selezione
    const [activeViewType, setActiveViewType] = useState(() =>
        window.matchMedia("(max-width: 480px)").matches ? 'trimestre' : 'mese'
    );

    // Listener per tracciare Ctrl/Cmd key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.metaKey || e.ctrlKey) {
                modifierKeyRef.current = true;
            }
        };
        const handleKeyUp = (e) => {
            if (!e.metaKey && !e.ctrlKey) {
                modifierKeyRef.current = false;
            }
        };
        const handleBlur = () => {
            modifierKeyRef.current = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    useEffect(() => {
        if (props.hrData) {
            setDataset(props.hrData);
            setFilteredDataset(props.hrData);
            setSelectedPeriods([]);
        }
    }, [props.hrData]);

    // Notifica il padre al mount della vista attiva
    useEffect(() => {
        if (onViewTypeChange) {
            onViewTypeChange(activeViewType);
        }
    }, [onViewTypeChange]);

    // BAR CHART - Stacked Bar per Mesi/Trimestri/Anni con categorie ore

    // Funzione per raggruppare i dati in base al tipo di vista
    const getGroupedData = () => {
        if (activeViewType === 'mese') {
            // Vista Mensile - gestisce correttamente più anni
            const mesiMap = {
                'Gennaio': 1, 'Febbraio': 2, 'Marzo': 3, 'Aprile': 4,
                'Maggio': 5, 'Giugno': 6, 'Luglio': 7, 'Agosto': 8,
                'Settembre': 9, 'Ottobre': 10, 'Novembre': 11, 'Dicembre': 12
            };

            const mesiAbbreviati = {
                'Gennaio': 'Gen', 'Febbraio': 'Feb', 'Marzo': 'Mar', 'Aprile': 'Apr',
                'Maggio': 'Mag', 'Giugno': 'Giu', 'Luglio': 'Lug', 'Agosto': 'Ago',
                'Settembre': 'Set', 'Ottobre': 'Ott', 'Novembre': 'Nov', 'Dicembre': 'Dic'
            };

            // Crea una lista di mesi unici con anno
            const mesiAnnoSet = new Set();
            filteredDataset.forEach(d => {
                mesiAnnoSet.add(`${d.mese}-${d.anno}`);
            });

            // Converti in array e ordina cronologicamente
            const mesiAnnoArray = Array.from(mesiAnnoSet).map(meseAnno => {
                const [mese, anno] = meseAnno.split('-');
                return {
                    mese,
                    anno: parseInt(anno),
                    numeroMese: mesiMap[mese],
                    label: `${mesiAbbreviati[mese]} ${anno}`,
                    key: meseAnno
                };
            }).sort((a, b) => {
                if (a.anno !== b.anno) return a.anno - b.anno;
                return a.numeroMese - b.numeroMese;
            });

            const periodi = mesiAnnoArray.map(ma => ma.label);

            const oreLavorate = mesiAnnoArray.map(ma => {
                const total = filteredDataset
                    .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                    .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreNonLavorate = mesiAnnoArray.map(ma => {
                const total = filteredDataset
                    .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                    .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreDaContratto = mesiAnnoArray.map(ma => {
                const total = filteredDataset
                    .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                    .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
                return Number(total.toFixed(1));
            });

            return {
                periodi,
                oreLavorate,
                oreNonLavorate,
                oreDaContratto,
                mesiAnnoArray // Passa anche le informazioni complete per tooltip e click
            };

        } else if (activeViewType === 'trimestre') {
            // Vista Trimestrale
            const trimestri = [...new Set(filteredDataset.map(d => d.trimestre))].sort((a, b) => {
                // a e b sono stringhe tipo "Trim 1 - 2025"
                // Divido la stringa per ottenere le parti
                const partiA = a.split(' '); // ["Trim", "1", "-", "2025"]
                const partiB = b.split(' ');

                const annoA = parseInt(partiA[3]); // 2025
                const annoB = parseInt(partiB[3]);
                const trimNumA = parseInt(partiA[1]); // 1
                const trimNumB = parseInt(partiB[1]);

                // Prima ordino per anno
                if (annoA !== annoB) {
                    return annoA - annoB;
                }
                // Se stesso anno, ordino per numero trimestre
                return trimNumA - trimNumB;
            });

            const oreLavorate = trimestri.map(trimestre => {
                const total = filteredDataset
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreNonLavorate = trimestri.map(trimestre => {
                const total = filteredDataset
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreDaContratto = trimestri.map(trimestre => {
                const total = filteredDataset
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
                return Number(total.toFixed(1));
            });

            return { periodi: trimestri, oreLavorate, oreNonLavorate, oreDaContratto };

        } else if (activeViewType === 'anno') {
            // Vista Annuale
            const anni = [...new Set(filteredDataset.map(d => d.anno))].sort();

            const oreLavorate = anni.map(anno => {
                const total = filteredDataset
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreNonLavorate = anni.map(anno => {
                const total = filteredDataset
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreDaContratto = anni.map(anno => {
                const total = filteredDataset
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
                return Number(total.toFixed(1));
            });

            return { periodi: anni, oreLavorate, oreNonLavorate, oreDaContratto };
        }
    };

    const groupedData = getGroupedData();
    const { periodi, oreLavorate, oreNonLavorate, oreDaContratto } = groupedData;
    const mesiAnnoArray = groupedData.mesiAnnoArray || [];

    // Funzione per determinare se un periodo è selezionato
    const isPeriodSelected = (index) => {
        if (selectedPeriods.length === 0) return false;

        let periodKey;
        if (activeViewType === 'mese') {
            const meseAnno = mesiAnnoArray[index];
            periodKey = meseAnno ? `${meseAnno.mese}-${meseAnno.anno}` : null;
        } else {
            periodKey = String(periodi[index]);
        }

        return selectedPeriods.some(p => p.key === periodKey);
    };

    // Genera i dati con stili per evidenziare le barre selezionate
    const hasSelection = selectedPeriods.length > 0;

    const getBarDataWithStyles = (values, normalGradient, selectedGradient, dimmedGradient, borderRadius) => {
        return values.map((value, index) => {
            const isSelected = isPeriodSelected(index);
            let color;

            if (hasSelection) {
                color = isSelected ? selectedGradient : dimmedGradient;
            } else {
                color = normalGradient;
            }

            return {
                value,
                itemStyle: {
                    color,
                    borderRadius
                }
            };
        });
    };

    // Gradienti per Ore Lavorate (blu)
    const oreLavorateNormal = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(59, 130, 246, 0.85)' },
        { offset: 0.5, color: 'rgba(1, 111, 183, 0.7)' },
        { offset: 1, color: 'rgba(1, 72, 163, 0.55)' }
    ]);
    const oreLavorateSelected = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(59, 130, 246, 1)' },
        { offset: 0.5, color: 'rgba(1, 111, 183, 0.95)' },
        { offset: 1, color: 'rgba(1, 72, 163, 0.85)' }
    ]);
    const oreLavorateDimmed = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
        { offset: 0.5, color: 'rgba(1, 111, 183, 0.25)' },
        { offset: 1, color: 'rgba(1, 72, 163, 0.2)' }
    ]);

    // Gradienti per Ore Non Lavorate (verde lime)
    const oreNonLavorateNormal = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(163, 230, 53, 0.85)' },
        { offset: 0.5, color: 'rgba(132, 204, 22, 0.7)' },
        { offset: 1, color: 'rgba(101, 163, 13, 0.55)' }
    ]);
    const oreNonLavorateSelected = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(163, 230, 53, 1)' },
        { offset: 0.5, color: 'rgba(132, 204, 22, 0.95)' },
        { offset: 1, color: 'rgba(101, 163, 13, 0.85)' }
    ]);
    const oreNonLavorateDimmed = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(163, 230, 53, 0.3)' },
        { offset: 0.5, color: 'rgba(132, 204, 22, 0.25)' },
        { offset: 1, color: 'rgba(101, 163, 13, 0.2)' }
    ]);

    const series = [
        {
            name: 'Ore lavorate',
            type: 'bar',
            stack: 'total',
            data: getBarDataWithStyles(oreLavorate, oreLavorateNormal, oreLavorateSelected, oreLavorateDimmed, [0, 0, 0, 0]),
            label: {
                show: true,
                position: 'inside',
                formatter: (params) => {
                    const val = typeof params.value === 'object' ? params.value.value : params.value;
                    return val > 0 ? formatNumber(val) : '';
                },
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 10,
                textShadowColor: 'rgba(0, 0, 0, 0.3)',
                textShadowBlur: 2
            },
            emphasis: {
                focus: 'series',
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(96, 165, 250, 0.95)' },
                        { offset: 0.5, color: 'rgba(37, 99, 235, 0.85)' },
                        { offset: 1, color: 'rgba(29, 78, 216, 0.75)' }
                    ])
                }
            },
            cursor: 'pointer'
        },
        {
            name: 'Ore non lavorate',
            type: 'bar',
            stack: 'total',
            data: getBarDataWithStyles(oreNonLavorate, oreNonLavorateNormal, oreNonLavorateSelected, oreNonLavorateDimmed, [4, 4, 0, 0]),
            label: {
                show: true,
                position: 'inside',
                formatter: (params) => {
                    const val = typeof params.value === 'object' ? params.value.value : params.value;
                    return val > 0 ? formatNumber(val) : '';
                },
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 10,
                textShadowColor: 'rgba(0, 0, 0, 0.3)',
                textShadowBlur: 2
            },
            emphasis: {
                focus: 'series',
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(190, 242, 100, 0.95)' },
                        { offset: 0.5, color: 'rgba(163, 230, 53, 0.85)' },
                        { offset: 1, color: 'rgba(132, 204, 22, 0.75)' }
                    ])
                }
            },
            cursor: 'pointer'
        },
        {
            name: 'Ore da contratto',
            type: 'line',
            data: oreDaContratto,
            lineStyle: {
                color: 'rgba(245, 158, 11, 0.8)',
                width: 3,
                shadowColor: 'rgba(245, 158, 11, 0.3)',
                shadowBlur: 6,
                shadowOffsetY: 2
            },
            itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(251, 191, 36, 0.9)' },
                    { offset: 1, color: 'rgba(245, 158, 11, 0.8)' }
                ]),
                borderColor: 'rgba(255, 255, 255, 0.9)',
                borderWidth: 2
            },
            symbol: 'circle',
            symbolSize: 8,
            smooth: false,
            emphasis: {
                focus: 'series',
                itemStyle: {
                    color: 'rgba(251, 191, 36, 0.95)',
                    borderColor: 'rgba(245, 158, 11, 0.9)',
                    borderWidth: 3,
                    shadowColor: 'rgba(245, 158, 11, 0.5)',
                    shadowBlur: 10
                }
            },
        }
    ];

    const barOptions = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: [12, 16],
            textStyle: {
                color: '#334155',
                fontSize: 12
            },
            extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
            formatter: function (params) {
                let result = `<div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: #1e293b;">${params[0].axisValue}</div>`;

                // Calcola il totale ore per questo periodo in base alla vista attiva
                const periodo = params[0].axisValue;
                const dataIndex = params[0].dataIndex;
                let totaleOreLavorate, totaleOreNonLavorate;

                if (activeViewType === 'mese') {
                    // Usa mesiAnnoArray per ottenere mese e anno corretti
                    const meseAnno = mesiAnnoArray[dataIndex];
                    if (meseAnno) {
                        totaleOreLavorate = filteredDataset
                            .filter(d => d.mese === meseAnno.mese && d.anno === meseAnno.anno)
                            .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                        totaleOreNonLavorate = filteredDataset
                            .filter(d => d.mese === meseAnno.mese && d.anno === meseAnno.anno)
                            .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                    }
                } else if (activeViewType === 'trimestre') {
                    totaleOreLavorate = filteredDataset
                        .filter(d => d.trimestre === periodo)
                        .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                    totaleOreNonLavorate = filteredDataset
                        .filter(d => d.trimestre === periodo)
                        .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                } else if (activeViewType === 'anno') {
                    totaleOreLavorate = filteredDataset
                        .filter(d => d.anno === periodo)
                        .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                    totaleOreNonLavorate = filteredDataset
                        .filter(d => d.anno === periodo)
                        .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                }

                const totale = totaleOreLavorate + totaleOreNonLavorate;
                params.forEach(param => {
                    const val = typeof param.value === 'object' ? param.value.value : param.value;
                    result += `<div style="display: flex; justify-content: space-between; margin: 4px 0;">${param.marker} <span style="flex: 1; margin-left: 8px;">${param.seriesName}</span><span style="font-weight: 600; margin-left: 16px;">${formatNumber(val)} h</span></div>`;
                });
                result += `<div style="border-top: 1px solid #e2e8f0; margin-top: 8px; padding-top: 8px; font-weight: 600; color: #016FB7;">Totale: ${formatNumber(totale)} h</div>`;
                if (!hasSelection) {
                    result += '<div style="margin-top: 8px; font-size: 10px; color: #94a3b8;">Click per filtrare • Cmd/Ctrl+Click per multi-selezione</div>';
                }
                return result;
            }
        },
        legend: {
            data: ['Ore lavorate', 'Ore non lavorate', 'Ore da contratto'],
            bottom: 0
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '50',
            top: '30',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: periodi,
            axisLabel: {
                fontSize: 10,
                rotate: 45
            }
        },
        yAxis: {
            type: 'value',
            name: `Ore`,
            axisLabel: {
                fontSize: 11,
                formatter: (value) => formatNumber(value)
            }
        },
        series: series,
        animationDuration: 1000,
        animationEasing: 'elasticOut',
        animationDelay: function (idx) {
            return idx * 50;
        }
    };

    // HANDLERS
    const handleBarClick = (e) => {
        if (!e.data) return;

        // Verifica se Ctrl/Cmd è premuto usando il ref
        const isMultiSelect = modifierKeyRef.current;

        // Crea l'oggetto filtro per il periodo cliccato
        let clickedFilter;
        if (activeViewType === 'mese') {
            const meseAnno = mesiAnnoArray[e.dataIndex];
            if (meseAnno) {
                clickedFilter = {
                    type: 'mese',
                    value: meseAnno.mese,
                    anno: meseAnno.anno,
                    key: `${meseAnno.mese}-${meseAnno.anno}`
                };
            }
        } else if (activeViewType === 'trimestre') {
            clickedFilter = {
                type: 'trimestre',
                value: periodi[e.dataIndex],
                key: periodi[e.dataIndex]
            };
        } else if (activeViewType === 'anno') {
            clickedFilter = {
                type: 'anno',
                value: periodi[e.dataIndex],
                key: String(periodi[e.dataIndex])
            };
        }

        if (!clickedFilter) return;

        let newSelectedPeriods;

        if (isMultiSelect) {
            // Multi-selezione: toggle del periodo
            const existingIndex = selectedPeriods.findIndex(p => p.key === clickedFilter.key);
            if (existingIndex >= 0) {
                // Rimuovi dalla selezione
                newSelectedPeriods = selectedPeriods.filter(p => p.key !== clickedFilter.key);
            } else {
                // Aggiungi alla selezione
                newSelectedPeriods = [...selectedPeriods, clickedFilter];
            }
        } else {
            // Click singolo
            if (selectedPeriods.length === 1 && selectedPeriods[0].key === clickedFilter.key) {
                // Se è già l'unico selezionato, deseleziona tutto
                newSelectedPeriods = [];
            } else {
                // Altrimenti seleziona solo questo
                newSelectedPeriods = [clickedFilter];
            }
        }

        setSelectedPeriods(newSelectedPeriods);

        // Notifica il padre del cambio filtro
        if (onChartFilterChange) {
            if (newSelectedPeriods.length === 0) {
                onChartFilterChange(null);
            } else {
                onChartFilterChange(newSelectedPeriods);
            }
        }
    };

    const handleViewTypeClick = (viewType) => {
        setActiveViewType(viewType);
        // Reset della selezione quando cambia la vista
        if (selectedPeriods.length > 0) {
            setSelectedPeriods([]);
            // Notifica il padre che il filtro è stato rimosso
            if (onChartFilterChange) {
                onChartFilterChange(null);
            }
        }
        if (onViewTypeChange) {
            onViewTypeChange(viewType);
        }
    };

    const handleLegendSelectChanged = (params) => {
        // params.selected contiene lo stato di tutte le serie { "Ore lavorate": true, "Ore non lavorate": false, ... }
        if (onLegendChange) {
            onLegendChange(params.selected);
        }
    };

    const handleDownloadFile = () => {
        if (!onDownload) return;

        // Prepara i dati del grafico nel formato richiesto dal backend
        const chartData = {
            viewType: activeViewType,
            periodi: periodi,
            rows: [
                {
                    categoria: 'Ore Lavorate',
                    valori: oreLavorate,
                    totale: Number(oreLavorate.reduce((sum, val) => sum + val, 0).toFixed(1))
                },
                {
                    categoria: 'Ore Non Lavorate',
                    valori: oreNonLavorate,
                    totale: Number(oreNonLavorate.reduce((sum, val) => sum + val, 0).toFixed(1))
                },
                {
                    categoria: 'Ore da Contratto',
                    valori: oreDaContratto,
                    totale: Number(oreDaContratto.reduce((sum, val) => sum + val, 0).toFixed(1))
                }
            ]
        };

        // Chiama la funzione di download passata dal padre
        onDownload(chartData);
    }

    return (
        <>
            <div className='bar-chart-title-container'>
                <div className='title-view-type-container'>
                    <span className="bar-chart-title">Trend Ore Consuntivate vs Pianificate</span>
                    <div className='bar-chart-view-type-excel'>
                        <div className='view-type-container'>
                            <button
                                type="button"
                                className={`view-type-btn ${activeViewType === 'mese' ? 'active' : ''}`}
                                onClick={() => handleViewTypeClick('mese')}
                                title="Vista Mensile"
                            >
                                <span>M</span>
                            </button>
                            <button
                                type="button"
                                className={`view-type-btn ${activeViewType === 'trimestre' ? 'active' : ''}`}
                                onClick={() => handleViewTypeClick('trimestre')}
                                title="Vista Trimestrale"
                            >
                                <span>T</span>
                            </button>
                            <button
                                type="button"
                                className={`view-type-btn ${activeViewType === 'anno' ? 'active' : ''}`}
                                onClick={() => handleViewTypeClick('anno')}
                                title="Vista Annuale"
                            >
                                <span>A</span>
                            </button>
                        </div>
                        <div className="esporta-file-excel">
                            <button className="hera-download-btn" onClick={handleDownloadFile} disabled={dataset.length === 0}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1 -2 2H5a2 2 0 0 1 -2 -2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Esporta
                            </button>
                        </div>
                        {extraButton}
                    </div>
                </div>
                {dataset.length === 0 ? (
                    <div className="chart-empty-state">
                        <div className="chart-empty-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M3 3v18h18"></path>
                                <path d="M18 9l-5 5-4-4-3 3"></path>
                            </svg>
                        </div>
                        <h4 className="chart-empty-title">Nessun dato da visualizzare</h4>
                        <p className="chart-empty-text">
                            Seleziona un intervallo di date e applica i filtri per visualizzare il grafico.
                        </p>
                    </div>
                ) : (
                    <ReactEChartsCore
                        ref={barRef}
                        echarts={echarts}
                        option={barOptions}
                        onEvents={{
                            click: handleBarClick,
                            legendselectchanged: handleLegendSelectChanged
                        }}
                        style={{ height: 400 }}
                    />
                )}
            </div>
        </>
    )
}

export default TrendPeriodoChart;